import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { creditos, fechaHora, hora } from "../lib/formato";
import {
  Aviso,
  Cabecera,
  Campo,
  Cargando,
  Etiqueta,
  Modal,
  Panel,
  TarjetaDato,
  Vacio,
} from "../componentes/piezas";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

/** Un cobro en efectivo que no cayo en ningun turno. */
interface Suelto {
  id: string;
  tipo: string;
  fecha: string;
  concepto: string;
  /** Con el signo del cajon: un gasto pagado en mano viene en negativo. */
  centimos: number;
}

interface FueraDeArqueo {
  desde: string;
  hasta: string;
  cuantos: number;
  centimos: number;
  apuntes: Suelto[];
  hayMas: boolean;
}

interface Sesion {
  id: string;
  aperturaFecha: string;
  saldoInicialCentimos: number;
  cierreFecha: string | null;
  saldoTeoricoCentimos: number | null;
  saldoRealCentimos: number | null;
  diferenciaCentimos: number | null;
  estado: string;
  notas: string | null;
  usuario?: { nombre: string } | null;
}

interface Actual {
  sesion: Sesion | null;
  movimientos?: {
    id: string;
    fecha: string;
    tipo: string;
    importeCentimos: number;
    concepto: string;
    usuario: { nombre: string } | null;
  }[];
  teoricoCentimos?: number;
  porTipo?: Record<string, number>;
}

export default function Caja() {
  const { t } = useIdioma();
  const clienteConsultas = useQueryClient();
  const [dialogo, setDialogo] = useState<
    "abrir" | "cerrar" | "movimiento" | null
  >(null);
  const [error, setError] = useState("");

  const actual = useQuery({
    queryKey: ["caja-actual"],
    queryFn: () => api<Actual>("/caja/actual"),
  });
  const sesiones = useQuery({
    queryKey: ["caja-sesiones"],
    queryFn: () => api<Sesion[]>("/caja/sesiones"),
  });

  const fueraDeArqueo = useQuery({
    queryKey: ["caja-fuera-de-arqueo"],
    queryFn: () => api<FueraDeArqueo>("/caja/fuera-de-arqueo"),
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ["caja-fuera-de-arqueo"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["caja-actual"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["caja-sesiones"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["panel"] });
  };

  if (actual.isLoading) return <Cargando />;
  const sesion = actual.data?.sesion ?? null;
  /** Abiertas que no son la mia: las de otros puestos. */
  const otrasAbiertas = (sesiones.data ?? []).filter(
    (s) => s.estado === "ABIERTA" && s.id !== sesion?.id,
  ).length;

  return (
    <>
      <Cabecera titulo={t("c.titulo")} descripcion={t("c.descripcion")}>
        {sesion ? (
          <>
            <button
              className="boton secundario"
              onClick={() => setDialogo("movimiento")}
            >
              {t("c.entradaSalida")}
            </button>
            <button className="boton" onClick={() => setDialogo("cerrar")}>
              {t("c.cerrarCaja")}
            </button>
          </>
        ) : (
          <button className="boton" onClick={() => setDialogo("abrir")}>
            {t("c.abrirCaja")}
          </button>
        )}
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>

      {!sesion ? (
        <Aviso tipo="atencion">
          {/* «Tu» y no «la»: con puestos puede haber otra caja abierta en la
              barra mientras la del mostrador esta cerrada, y decir que no hay
              ninguna era mentira -y el historial de abajo lo desmentia-. */}
          {t("c.sinCaja")}
          {otrasAbiertas > 0 &&
            ` ${otrasAbiertas === 1 ? t("c.otraAbierta") : t("c.otrasAbiertas", { n: otrasAbiertas })}`}
        </Aviso>
      ) : (
        <>
          <div className="rejilla cuatro" style={{ marginBottom: 16 }}>
            <TarjetaDato
              etiqueta={t("c.fondoInicial")}
              valor={creditos(sesion.saldoInicialCentimos)}
              pie={t("c.abiertaALas", { hora: hora(sesion.aperturaFecha) })}
            />
            <TarjetaDato
              etiqueta={t("c.dispensaciones")}
              valor={creditos(actual.data?.porTipo?.DISPENSACION ?? 0)}
            />
            <TarjetaDato
              etiqueta={t("c.barra")}
              valor={creditos(actual.data?.porTipo?.BARRA ?? 0)}
            />
            <TarjetaDato
              etiqueta={t("c.cuotasYRecargas")}
              valor={creditos(
                (actual.data?.porTipo?.CUOTA ?? 0) +
                  (actual.data?.porTipo?.RECARGA ?? 0),
              )}
            />
            <TarjetaDato
              etiqueta={t("c.deberiaHaber")}
              valor={creditos(actual.data?.teoricoCentimos ?? 0)}
              pie={t("c.formula")}
            />
          </div>

          <Panel titulo={t("c.movimientos")} sinCuerpo>
            {!actual.data?.movimientos ||
            actual.data.movimientos.length === 0 ? (
              <Vacio>{t("c.sinMovimientos")}</Vacio>
            ) : (
              <div className="tabla-envoltorio">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>{t("c.hora")}</th>
                      <th>{t("c.tipo")}</th>
                      <th>{t("c.concepto")}</th>
                      <th>{t("c.usuario")}</th>
                      <th className="num">{t("c.importe")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actual.data.movimientos.map((m) => (
                      <tr key={m.id}>
                        <td className="pequeno suave">{hora(m.fecha)}</td>
                        <td>
                          <Etiqueta
                            tipo={m.importeCentimos >= 0 ? "verde" : "roja"}
                          >
                            {t(`movimiento.${m.tipo}` as Clave)}
                          </Etiqueta>
                        </td>
                        <td className="pequeno">{m.concepto}</td>
                        <td className="pequeno suave">
                          {m.usuario?.nombre ?? "—"}
                        </td>
                        <td
                          className="num tabular"
                          style={{
                            color:
                              m.importeCentimos >= 0
                                ? "var(--verde)"
                                : "var(--rojo)",
                          }}
                        >
                          {m.importeCentimos >= 0 ? "+" : ""}
                          {creditos(m.importeCentimos)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}

      {(fueraDeArqueo.data?.cuantos ?? 0) > 0 && (
        <div style={{ marginTop: 16 }}>
          <Panel titulo={t("c.fa.titulo")} sinCuerpo>
            <div style={{ padding: "12px 14px 0" }}>
              <Aviso tipo="atencion">
                {/* Dos frases enteras, no una con la palabra en un hueco:
                    «1 cobros» es justo lo que el barrido de plurales vigila. */}
                {fueraDeArqueo.data!.cuantos === 1
                  ? t("c.fa.explicacionUno", {
                      importe: creditos(fueraDeArqueo.data!.centimos),
                    })
                  : t("c.fa.explicacion", {
                      cuantos: fueraDeArqueo.data!.cuantos,
                      importe: creditos(fueraDeArqueo.data!.centimos),
                    })}
              </Aviso>
            </div>
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("c.fa.cuando")}</th>
                    <th>{t("c.fa.que")}</th>
                    <th className="num">{t("c.fa.importe")}</th>
                  </tr>
                </thead>
                <tbody>
                  {fueraDeArqueo.data!.apuntes.map((a) => (
                    <tr key={`${a.tipo}-${a.id}`}>
                      <td className="pequeno">{fechaHora(a.fecha)}</td>
                      <td>
                        <Etiqueta>{t(`c.fa.tipo.${a.tipo}` as Clave)}</Etiqueta>{" "}
                        <span className="suave pequeno">{a.concepto}</span>
                      </td>
                      <td className="num tabular">{creditos(a.centimos)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {fueraDeArqueo.data!.hayMas && (
              <div className="suave pequeno" style={{ padding: "0 14px 12px" }}>
                {t("c.fa.hayMas")}
              </div>
            )}
          </Panel>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <Panel titulo={t("c.historial")} sinCuerpo>
          {sesiones.isLoading ? (
            <Cargando />
          ) : !sesiones.data || sesiones.data.length === 0 ? (
            <Vacio>{t("c.sinTurnos")}</Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("c.apertura")}</th>
                    <th>{t("c.cierre")}</th>
                    <th>{t("c.responsable")}</th>
                    <th className="num">{t("c.teorico")}</th>
                    <th className="num">{t("c.contado")}</th>
                    <th className="num">{t("c.diferencia")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sesiones.data.map((s) => (
                    <tr key={s.id}>
                      <td className="pequeno">{fechaHora(s.aperturaFecha)}</td>
                      <td className="pequeno">
                        {s.cierreFecha ? (
                          fechaHora(s.cierreFecha)
                        ) : (
                          <Etiqueta tipo="verde">{t("c.abierta")}</Etiqueta>
                        )}
                      </td>
                      <td className="pequeno suave">
                        {s.usuario?.nombre ?? "—"}
                      </td>
                      <td className="num tabular">
                        {s.saldoTeoricoCentimos !== null
                          ? creditos(s.saldoTeoricoCentimos)
                          : "—"}
                      </td>
                      <td className="num tabular">
                        {s.saldoRealCentimos !== null
                          ? creditos(s.saldoRealCentimos)
                          : "—"}
                      </td>
                      <td className="num tabular">
                        {s.diferenciaCentimos === null ? (
                          "—"
                        ) : s.diferenciaCentimos === 0 ? (
                          <Etiqueta tipo="verde">{t("c.cuadra")}</Etiqueta>
                        ) : (
                          <Etiqueta tipo="roja">
                            {s.diferenciaCentimos > 0 ? "+" : ""}
                            {creditos(s.diferenciaCentimos)}
                          </Etiqueta>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {dialogo === "abrir" && (
        <DialogoAbrir
          alCerrar={() => setDialogo(null)}
          alFallar={setError}
          alHecho={() => {
            setDialogo(null);
            refrescar();
          }}
        />
      )}
      {dialogo === "cerrar" && sesion && (
        <DialogoCerrar
          teorico={actual.data?.teoricoCentimos ?? 0}
          alCerrar={() => setDialogo(null)}
          alFallar={setError}
          alHecho={() => {
            setDialogo(null);
            refrescar();
          }}
        />
      )}
      {dialogo === "movimiento" && (
        <DialogoMovimiento
          alCerrar={() => setDialogo(null)}
          alFallar={setError}
          alHecho={() => {
            setDialogo(null);
            refrescar();
          }}
        />
      )}
    </>
  );
}

function DialogoAbrir({
  alCerrar,
  alHecho,
  alFallar,
}: {
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (m: string) => void;
}) {
  const { t } = useIdioma();
  const [saldo, setSaldo] = useState("100");
  const [notas, setNotas] = useState("");
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("c.abrir.titulo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cancelar")}
          </button>
          <button
            className="boton"
            disabled={enviando}
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/caja/abrir", {
                  metodo: "POST",
                  cuerpo: { saldoInicialEuros: Number(saldo || 0), notas },
                });
                alHecho();
              } catch (fallo) {
                alFallar(
                  fallo instanceof Error ? fallo.message : t("c.abrir.fallo"),
                );
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("c.abrir.boton")}
          </button>
        </>
      }
    >
      <Campo etiqueta={t("c.abrir.fondo")} ayuda={t("c.abrir.fondoAyuda")}>
        <input
          type="number"
          step="0.01"
          min={0}
          value={saldo}
          onChange={(e) => setSaldo(e.target.value)}
          autoFocus
        />
      </Campo>
      <Campo etiqueta={t("c.abrir.notas")}>
        <input
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          placeholder={t("c.abrir.notasEjemplo")}
        />
      </Campo>
    </Modal>
  );
}

function DialogoCerrar({
  teorico,
  alCerrar,
  alHecho,
  alFallar,
}: {
  teorico: number;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (m: string) => void;
}) {
  const { t } = useIdioma();
  const [real, setReal] = useState("");
  const [notas, setNotas] = useState("");
  const [enviando, setEnviando] = useState(false);
  const diferencia =
    real === "" ? null : Math.round(Number(real) * 100) - teorico;

  return (
    <Modal
      titulo={t("c.cerrar.titulo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cancelar")}
          </button>
          <button
            className="boton"
            disabled={enviando || real === ""}
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/caja/cerrar", {
                  metodo: "POST",
                  cuerpo: { saldoRealEuros: Number(real), notas },
                });
                alHecho();
              } catch (fallo) {
                alFallar(
                  fallo instanceof Error ? fallo.message : t("c.cerrar.fallo"),
                );
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("c.cerrar.boton")}
          </button>
        </>
      }
    >
      <div className="fila separada" style={{ marginBottom: 14 }}>
        <span className="suave">{t("c.cerrar.segunSistema")}</span>
        <strong className="tabular">{creditos(teorico)}</strong>
      </div>

      <Campo etiqueta={t("c.cerrar.contado")}>
        <input
          type="number"
          step="0.01"
          min={0}
          value={real}
          onChange={(e) => setReal(e.target.value)}
          autoFocus
        />
      </Campo>

      {diferencia !== null && diferencia !== 0 && (
        <Aviso tipo={Math.abs(diferencia) > 500 ? "error" : "atencion"}>
          {t("c.cerrar.descuadre")} <strong>{creditos(diferencia)}</strong>{" "}
          {diferencia > 0 ? t("c.cerrar.sobra") : t("c.cerrar.falta")}
        </Aviso>
      )}
      {diferencia === 0 && <Aviso tipo="ok">{t("c.cerrar.cuadra")}</Aviso>}

      <Campo etiqueta={t("c.cerrar.observaciones")}>
        <textarea
          rows={2}
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
        />
      </Campo>
    </Modal>
  );
}

function DialogoMovimiento({
  alCerrar,
  alHecho,
  alFallar,
}: {
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (m: string) => void;
}) {
  const { t } = useIdioma();
  const [tipo, setTipo] = useState("SALIDA");
  const [importe, setImporte] = useState("");
  const [concepto, setConcepto] = useState("");
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("c.mov.titulo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cancelar")}
          </button>
          <button
            className="boton"
            disabled={
              enviando || !(Number(importe) > 0) || concepto.trim().length < 3
            }
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/caja/movimiento", {
                  metodo: "POST",
                  cuerpo: { tipo, importeEuros: Number(importe), concepto },
                });
                alHecho();
              } catch (fallo) {
                alFallar(
                  fallo instanceof Error ? fallo.message : t("c.mov.fallo"),
                );
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("c.mov.boton")}
          </button>
        </>
      }
    >
      <Campo etiqueta={t("c.mov.tipo")}>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="SALIDA">{t("c.mov.salida")}</option>
          <option value="ENTRADA">{t("c.mov.entrada")}</option>
        </select>
      </Campo>
      <p className="suave pequeno" style={{ marginTop: -6 }}>
        {t("c.mov.nota1")} <strong>{t("c.mov.gastos")}</strong>
        {t("c.mov.nota2")}
      </p>
      <Campo etiqueta={t("c.mov.importe")}>
        <input
          type="number"
          step="0.01"
          min={0}
          value={importe}
          onChange={(e) => setImporte(e.target.value)}
          autoFocus
        />
      </Campo>
      <Campo etiqueta={t("c.mov.concepto")}>
        <input
          value={concepto}
          onChange={(e) => setConcepto(e.target.value)}
          placeholder={t("c.mov.conceptoEjemplo")}
        />
      </Campo>
    </Modal>
  );
}
